import { PaymentCB, PaymentCBRef } from "@/components/CashRegister/paymentCB"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useCashRegistersQuery } from "@/query/cashRegisterQuery"
import { useCurrentUserQuery } from "@/query/currentUserQuery"
import { usePaymentCashMutation } from "@/query/paymentCashQuery"
import {
  addCompletedPayment,
  CompletedPayment,
  initializePaymentProgress,
} from "@/store/cashRegisterReducers/cashRegisterSlice"
import { useAppDispatch, useAppSelector } from "@/store/store"
import { getCurrentCashRegister } from "@/utils/functions"
import {
  Banknote,
  CheckCircle,
  CreditCard,
  Plus,
  Printer,
  Trash2,
} from "lucide-react"
import React, { useEffect, useRef, useState } from "react"
import { v4 as uuidv4 } from "uuid"

type PaymentType = "cash" | "card"

interface PaymentContainer {
  id: string
  type: PaymentType
  amount: string
  isValidated: boolean
  isProcessing?: boolean
  error?: string
  showStripeTerminal?: boolean
}

export const UnifiedPaymentStep: React.FC = () => {
  const dispatch = useAppDispatch()
  const { collectionInfo } = useAppSelector((state) => state.cashRegister)
  const [paymentContainers, setPaymentContainers] = useState<
    PaymentContainer[]
  >([{ id: uuidv4(), type: "card", amount: "", isValidated: false }])
  const [showConfirmation, setShowConfirmation] = useState(false)
  const paymentCBRefs = useRef<{ [key: string]: PaymentCBRef | null }>({})
  const paymentCashMutation = usePaymentCashMutation()

  const { cashRegisters } = useCashRegistersQuery()
  const { currentUser } = useCurrentUserQuery()

  // Hook pour gérer Stripe Terminal (sera initialisé lors de chaque paiement)

  // Calculer le montant total à payer
  const totalAmount = React.useMemo(() => {
    if (!collectionInfo?.serviceWithPromo) return 0
    return collectionInfo.serviceWithPromo
      .filter((service) => !service.cancelled)
      .reduce(
        (total, service) =>
          total +
          (service.discountApplied?.priceEffect.newPrice || service.price),
        0
      )
  }, [collectionInfo?.serviceWithPromo])

  // Initialiser le progress de paiement
  useEffect(() => {
    if (totalAmount > 0 && !collectionInfo?.paymentProgress) {
      dispatch(initializePaymentProgress(totalAmount))
    }
  }, [totalAmount, collectionInfo?.paymentProgress, dispatch])

  const completedPayments =
    collectionInfo?.paymentProgress?.completedPayments || []
  const remainingAmount =
    collectionInfo?.paymentProgress?.remainingAmount || totalAmount
  const isFullyPaid = remainingAmount <= 0

  const currentCashRegister = getCurrentCashRegister(currentUser, cashRegisters)

  // Ajouter un nouveau conteneur de paiement
  const addPaymentContainer = () => {
    setPaymentContainers([
      ...paymentContainers,
      { id: uuidv4(), type: "card", amount: "", isValidated: false },
    ])
  }

  // Supprimer un conteneur de paiement
  const removePaymentContainer = (id: string) => {
    setPaymentContainers(paymentContainers.filter((p) => p.id !== id))
    // Nettoyer la référence PaymentCB
    delete paymentCBRefs.current[id]
  }

  // Mettre à jour un conteneur de paiement
  const updatePaymentContainer = (
    id: string,
    updates: Partial<PaymentContainer>
  ) => {
    setPaymentContainers(
      paymentContainers.map((p) => (p.id === id ? { ...p, ...updates } : p))
    )
  }

  // Valider un paiement
  const validatePayment = async (container: PaymentContainer) => {
    const amount = parseFloat(container.amount)

    if (isNaN(amount) || amount <= 0) {
      updatePaymentContainer(container.id, {
        error: "Montant invalide",
      })
      return
    }

    if (amount > remainingAmount) {
      updatePaymentContainer(container.id, {
        error: `Le montant dépasse le reste à payer (${remainingAmount.toFixed(
          2
        )}€)`,
      })
      return
    }

    updatePaymentContainer(container.id, {
      isProcessing: true,
      error: undefined,
    })

    try {
      let paymentIntentId: string | undefined

      // Pour les paiements par carte, afficher le terminal Stripe
      if (container.type === "card") {
        updatePaymentContainer(container.id, {
          showStripeTerminal: true,
          isProcessing: false,
        })
        return // Le paiement sera géré par le composant PaymentCB
      }

      // Pour les paiements en espèces, utiliser la mutation cash
      if (container.type === "cash") {
        if (!currentCashRegister?.caisseId) {
          updatePaymentContainer(container.id, {
            error: "Informations de caisse manquantes",
            isProcessing: false,
          })
          return
        }

        const result = await paymentCashMutation.mutateAsync({
          amount: Math.round(amount * 100), // Convertir en centimes
          currency: "EUR",
          cashRegisterId: currentCashRegister.caisseId,
          services: collectionInfo?.serviceWithPromo || [],
        })

        if (result.success) {
          paymentIntentId = result.paymentId

          // Ajouter le paiement complété
          const completedPayment: CompletedPayment = {
            id: result.paymentId,
            type: "cash",
            amount,
            timestamp: new Date().getTime(),
            paymentIntentId,
          }

          dispatch(addCompletedPayment(completedPayment))
          updatePaymentContainer(container.id, {
            isValidated: true,
            isProcessing: false,
          })

          // Si le paiement est complet, afficher la confirmation
          if (result.status === "completed") {
            setShowConfirmation(true)
          }
        }
      }
    } catch (error) {
      updatePaymentContainer(container.id, {
        isProcessing: false,
        error: error instanceof Error ? error.message : "Erreur inconnue",
      })
    }
  }

  // Gérer le succès du paiement par carte
  const handleCardPaymentSuccess = (
    containerId: string,
    paymentIntentId: string
  ) => {
    const container = paymentContainers.find((p) => p.id === containerId)
    if (!container) return

    const amount = parseFloat(container.amount)
    const completedPayment: CompletedPayment = {
      id: container.id,
      type: "card",
      amount,
      timestamp: new Date().getTime(),
      paymentIntentId,
    }

    dispatch(addCompletedPayment(completedPayment))
    updatePaymentContainer(container.id, {
      isValidated: true,
      showStripeTerminal: false,
    })

    // Si le paiement est complet, afficher la confirmation
    if (remainingAmount - amount <= 0) {
      setShowConfirmation(true)
    }
  }

  // Gérer l'échec du paiement par carte
  const handleCardPaymentFailed = (containerId: string, error: string) => {
    updatePaymentContainer(containerId, {
      error,
      showStripeTerminal: false,
    })
  }

  // Gérer l'annulation du paiement par carte
  const handleCardPaymentCancelled = (containerId: string) => {
    updatePaymentContainer(containerId, {
      showStripeTerminal: false,
    })
  }

  // Imprimer le ticket
  const printReceipt = () => {
    // TODO: Implémenter l'impression du ticket
    console.log("Impression du ticket...")
  }

  // Vue de confirmation finale
  if (showConfirmation && isFullyPaid) {
    return (
      <div className="space-y-6 max-h-[calc(95vh-400px)] overflow-y-auto">
        <Card>
          <CardHeader className="text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <CardTitle className="text-2xl">Paiement Confirmé</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-center text-lg font-semibold">
                Montant total: {totalAmount.toFixed(2)}€
              </p>
            </div>

            {/* Détail des paiements */}
            <div className="space-y-2">
              <h3 className="font-medium">Détail des paiements:</h3>
              {completedPayments.map((payment) => (
                <div
                  key={payment.id}
                  className="flex items-center justify-between p-2 bg-gray-100 rounded"
                >
                  <div className="flex items-center gap-2">
                    {payment.type === "cash" ? (
                      <Banknote className="h-4 w-4 text-green-600" />
                    ) : (
                      <CreditCard className="h-4 w-4 text-blue-600" />
                    )}
                    <span className="text-sm">
                      {payment.type === "cash" ? "Espèces" : "Carte bancaire"}
                    </span>
                  </div>
                  <span className="font-medium">
                    {payment.amount.toFixed(2)}€
                  </span>
                </div>
              ))}
            </div>

            {/* Bouton d'impression */}
            <Button
              onClick={printReceipt}
              className="w-full"
              size="lg"
              variant="default"
            >
              <Printer className="h-5 w-5 mr-2" />
              Imprimer le ticket de caisse
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Vue normale avec les conteneurs de paiement
  return (
    <div className="space-y-6 max-h-[calc(95vh-400px)] overflow-y-auto">
      {/* Résumé du paiement */}
      <Card>
        <CardHeader>
          <CardTitle>Résumé du paiement</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span>Montant total:</span>
              <span className="font-semibold">{totalAmount.toFixed(2)}€</span>
            </div>
            <div className="flex justify-between">
              <span>Montant payé:</span>
              <span className="font-semibold text-green-600">
                {(totalAmount - remainingAmount).toFixed(2)}€
              </span>
            </div>
            <div className="flex justify-between">
              <span>Reste à payer:</span>
              <span className="font-semibold text-orange-600">
                {remainingAmount.toFixed(2)}€
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Paiements validés */}
      {completedPayments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Paiements validés</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {completedPayments.map((payment) => (
                <div
                  key={payment.id}
                  className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {payment.type === "cash" ? (
                      <Banknote className="h-5 w-5 text-green-600" />
                    ) : (
                      <CreditCard className="h-5 w-5 text-blue-600" />
                    )}
                    <div>
                      <p className="font-medium">
                        {payment.type === "cash" ? "Espèces" : "Carte bancaire"}
                      </p>
                      <p className="text-sm text-gray-600">
                        {payment.amount.toFixed(2)}€
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Conteneurs de paiement actifs */}
      {!isFullyPaid && (
        <div className="space-y-4">
          {paymentContainers
            .filter((p) => !p.isValidated)
            .map((container) => (
              <Card key={container.id}>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Sélection du type de paiement */}
                      <div className="space-y-2">
                        <Label>Type de paiement</Label>
                        <Select
                          value={container.type}
                          onValueChange={(value: PaymentType) =>
                            updatePaymentContainer(container.id, {
                              type: value,
                            })
                          }
                          disabled={container.isProcessing}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cash">
                              <div className="flex items-center gap-2">
                                <Banknote className="h-4 w-4" />
                                Espèces
                              </div>
                            </SelectItem>
                            <SelectItem value="card">
                              <div className="flex items-center gap-2">
                                <CreditCard className="h-4 w-4" />
                                Carte bancaire
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Montant */}
                      <div className="space-y-2">
                        <Label>Montant</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={container.amount}
                          onChange={(e) =>
                            updatePaymentContainer(container.id, {
                              amount: e.target.value,
                              error: undefined,
                            })
                          }
                          disabled={container.isProcessing}
                        />
                      </div>
                    </div>

                    {/* Erreur */}
                    {container.error && (
                      <Alert variant="destructive">
                        <AlertDescription>{container.error}</AlertDescription>
                      </Alert>
                    )}

                    {/* Actions */}
                    {!container.showStripeTerminal ? (
                      <div className="flex gap-2">
                        <Button
                          onClick={() => validatePayment(container)}
                          disabled={
                            !container.amount ||
                            container.isProcessing ||
                            parseFloat(container.amount) <= 0 ||
                            paymentCashMutation.isPending
                          }
                          className="flex-1"
                        >
                          {container.isProcessing ||
                          paymentCashMutation.isPending
                            ? "Traitement..."
                            : "Valider"}
                        </Button>
                        {paymentContainers.length > 1 && (
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => removePaymentContainer(container.id)}
                            disabled={container.isProcessing}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ) : (
                      // Afficher le terminal Stripe pour le paiement par carte
                      <div className="border-t pt-4">
                        <PaymentCB
                          ref={(ref) => {
                            if (ref) paymentCBRefs.current[container.id] = ref
                          }}
                          amount={parseFloat(container.amount)}
                          onPaymentSuccess={(paymentIntentId) =>
                            handleCardPaymentSuccess(
                              container.id,
                              paymentIntentId
                            )
                          }
                          onPaymentFailed={(error) =>
                            handleCardPaymentFailed(container.id, error)
                          }
                          onPaymentCancelled={() =>
                            handleCardPaymentCancelled(container.id)
                          }
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

          {/* Bouton pour ajouter un nouveau paiement */}
          {remainingAmount > 0 && (
            <Button
              onClick={addPaymentContainer}
              variant="outline"
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un nouveau paiement
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
